import { API_TYPES, get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { z } from "zod";

export const getWorkerOpenPlacementFiltersApiUrl = (workerId: string) => {
  return `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/open-placement-filters`;
};

function getWorkerOpenPlacementFiltersQueryKey(
  workerId: string,
  queryParams?: {
    filter?: {
      useHiringWorkerTypes?: boolean;
    };
  }
) {
  const baseKey = [getWorkerOpenPlacementFiltersApiUrl(workerId)];
  return queryParams ? [...baseKey, queryParams] : baseKey;
}

export const workerOpenPlacementFiltersResponseSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.worker),
    attributes: z.object({
      availablePlacementWorkerTypes: z.array(z.string()),
    }),
  }),
});
export type WorkerOpenPlacementFiltersResponse = z.infer<
  typeof workerOpenPlacementFiltersResponseSchema
>;

function transformResponse(response: WorkerOpenPlacementFiltersResponse): string[] {
  const { data } = response;
  return data.attributes.availablePlacementWorkerTypes;
}

interface UseGetWorkerOpenPlacementFiltersParams {
  workerId: string;
  useHiringWorkerTypes?: boolean;
}

export function useGetWorkerOpenPlacementFilters(
  params: UseGetWorkerOpenPlacementFiltersParams,
  options?: UseQueryOptions<string[]>
) {
  const { workerId, useHiringWorkerTypes } = params;

  const queryParams =
    useHiringWorkerTypes === undefined ? undefined : { filter: { useHiringWorkerTypes } };

  return useQuery<string[]>({
    queryKey: getWorkerOpenPlacementFiltersQueryKey(workerId, queryParams),
    queryFn: async () => {
      const response = await get({
        url: getWorkerOpenPlacementFiltersApiUrl(workerId),
        queryParams,
        responseSchema: workerOpenPlacementFiltersResponseSchema,
      });
      return transformResponse(response.data);
    },
    ...options,
  });
}
